import React, { useState, useEffect, useContext } from 'react';
import Layout from '../../components/layouts/Main';
import { AuthContext } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import { FaPlus, FaEye, FaSpinner } from 'react-icons/fa';
import { Card, Table, Badge, Form, Row, Col, Pagination } from 'react-bootstrap';
import config from '../../config';
 
const StockTransferInstanceList = () => {
  const { loggedUser: user } = useContext(AuthContext);
  const username = user?.username;
  const domainName = username;
  const apiUrl =  config.API_BASE_URL;

  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    status: '',
    product: ''
  });

  // Fetch instances with pagination and filters
  useEffect(() => {
    const fetchInstances = async () => {
      try {
        setLoading(true);
        
        let queryParams = `page=${currentPage}&limit=10`;
        if (filters.status) queryParams += `&status=${filters.status}`;
        if (filters.product) queryParams += `&product=${filters.product}`;
        
        const response = await fetch(`${apiUrl}/stock-transfer-instances?${queryParams}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-Subdomain': domainName
          },
          credentials: 'include'
        });

        const data = await response.json();
        if (data.success) {
          setInstances(data.instances || []);
          setTotalPages(data.totalPages || 1);
        } else {
          console.error('Failed to fetch instances:', data.message);
        }
      } catch (error) {
        console.error('Error fetching instances:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchInstances();
  }, [apiUrl, domainName, currentPage, filters]);

  // Fetch products for filter
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(`${apiUrl}/products`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-Subdomain': domainName
          },
          credentials: 'include'
        });

        const data = await response.json();
        if (data.products) {
          setProducts(data.products);
        }
      } catch (error) {
        console.error('Error fetching products:', error);
      }
    };

    fetchProducts();
  }, [apiUrl, domainName]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1); // Reset to first page when filter changes
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'new':
        return <Badge bg="primary">New</Badge>;
      case 'running':
        return <Badge bg="warning">Running</Badge>;
      case 'closed':
        return <Badge bg="success">Closed</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const items = [];
    for (let i = 1; i <= totalPages; i++) {
      items.push(
        <Pagination.Item 
          key={i} 
          active={i === currentPage}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    }

    return (
      <Pagination className="justify-content-center mt-4">
        <Pagination.Prev 
          onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
        />
        {items}
        <Pagination.Next 
          onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
        />
      </Pagination>
    );
  };

  return (
    <Layout>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Stock Transfers</h2>
        <Link to="/stock-transfer-instances/create" className="btn btn-primary">
          <FaPlus className="me-2" /> Create New Instance
        </Link>
      </div>

      {/* Filters */}
      <Card className="mb-4 d-none">
        <Card.Header>
          <h5 className="mb-0">Filters</h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                >
                  <option value="">All Statuses</option>
                  <option value="new">New</option>
                  <option value="running">Running</option>
                  <option value="closed">Closed</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Product</Form.Label>
                <Form.Select
                  name="product"
                  value={filters.product}
                  onChange={handleFilterChange}
                >
                  <option value="">All Products</option>
                  {products.map(product => (
                    <option key={product._id} value={product._id}>
                      {product.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Instances Table */}
      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <FaSpinner className="fa-spin me-2" />
              <span>Loading...</span>
            </div>
          ) : instances.length === 0 ? (
            <div className="alert alert-info">
              No stock transfer instances found. Create a new one to get started.
            </div>
          ) : (
            <>
              <Table responsive striped bordered hover>
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Source Warehouse</th>
                    <th>Quantity</th>
                    <th>Remaining</th>
                    <th>Status</th>
                    <th>Created Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {instances.map(instance => {
                    // const transfers = instance.transfers || [];
                    // const transferredQuantity = transfers.reduce(
                    //   (sum, t) => sum + (t.transferQuantity || 0), 
                    //   0
                    // );
                    const remainingQuantity = instance.quantity - instance.usedQuantity;
                    // const remainingQuantity = instance.quantity - transferredQuantity;
                    
                    return (
                      <tr key={instance._id}>
                        <td>{instance.product?.productName}</td>
                        <td>{instance.sourceWarehouse?.label}</td>
                        <td>{instance.quantity} {instance.unit}</td>
                        <td>{remainingQuantity} {instance.unit}</td>
                        <td>{getStatusBadge(instance.status)}</td>
                        <td>{new Date(instance.createdAt).toLocaleDateString()}</td>
                        <td>
                          <Link 
                            to={`/stock-transfer-instances/${instance._id}`}
                            className="btn btn-sm btn-info me-2"
                          >
                            <FaEye /> View
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              {renderPagination()}
            </>
          )}
        </Card.Body>
      </Card>
    </Layout>
  );
};

export default StockTransferInstanceList;